import React, { useState } from 'react';
import { getHPStatus } from '../utils/healthSystem';
import { getReadyTurnIns, getReadyTurnInStep, getOfferAt, effectivePartyLevel } from '../game/questEngine';
import { getShopStock } from '../data/shopStock';
import { buyPrice, sellPrice, canAfford, isSellable } from '../game/shopController';
import { ITEM_CATALOG, getRarityColor, describeHealAmount, describeSpellDamage } from '../utils/inventorySystem';
import { areRequirementsMet, isQuestItemSearchable } from '../game/milestoneEngine';

// A short "what this item does" line for the shop list, so a buyer sees the effect (e.g.
// "+2 attack", "Heals 2d4+2 HP (4 to 10)") next to the price instead of a bare name.
const shopEffectLabel = (item) => {
  if (!item) return null;
  if (item.type === 'weapon' || item.type === 'armor') return item.bonus || null; // "+2 attack" / "+2 defense"
  if (item.type === 'accessory') return item.bonus || '+1 to checks';
  if (item.effect === 'heal' && item.amount) return `Heals ${describeHealAmount(item.amount)}`;
  if (item.effect === 'spell' && item.damage) return `Deals ${describeSpellDamage(item.damage)}`;
  return null;
};

const getAbilityModifier = (score) => Math.floor(((score || 10) - 10) / 2);

const RESIDENTIAL_TYPES = ['house', 'manor', 'keep'];
const REST_BUILDING_TYPES = ['inn', 'tavern'];
const SHOP_BUILDING_TYPES = ['shop', 'market', 'blacksmith', 'alchemist', 'apothecary'];

const familyRoleLabel = (npc) => {
    if (npc.familyRole === 'head') return 'Head of Household';
    if (npc.familyRole === 'spouse') return 'Spouse';
    if (npc.familyRole === 'child') return 'Child';
    return null;
};

const genderIcon = (gender) => {
    if (gender === 'Male') return '\u2642';
    if (gender === 'Female') return '\u2640';
    return '';
};

const RESURRECTION_COST_PER_LEVEL = 25;

// Active deterministic NPC-talk milestones (type 'talk'): not completed, prerequisites
// met, and carrying an npc trigger. These drive the "Talk" button on the matching NPC
// row (and the building-level fallback when the NPC was never placed).
const getActiveTalkMilestones = (milestones) => {
    if (!Array.isArray(milestones)) return [];
    return milestones.filter(m =>
        m && typeof m === 'object' &&
        m.type === 'talk' && !m.completed && m.trigger?.npc &&
        areRequirementsMet(m, milestones)
    );
};

// Tab button styling for the building modal's section tabs (Visit | Wares).
const tabBtnStyle = (active) => ({
    flex: 1,
    padding: '10px 12px',
    cursor: 'pointer',
    fontWeight: 'bold',
    letterSpacing: '1px',
    fontFamily: 'var(--header-font)',
    border: 'none',
    borderBottom: `3px solid ${active ? 'var(--primary)' : 'var(--border)'}`,
    background: active ? 'var(--primary)' : 'transparent',
    color: active ? '#fff' : 'var(--text)',
    borderRadius: '6px 6px 0 0',
});

const BuildingModal = ({ building, npcs, onClose, firstHero, onQuestItemFound, onRest, onResurrect, onBuy, onSell, party, sideQuests, onAcceptSideQuest, onTurnInQuest, townName, milestones, onTalkToNpc }) => {
    const [imageError, setImageError] = useState(false);
    const [activeTab, setActiveTab] = useState('visit'); // building modal tabs: 'visit' | 'wares'
    const [tradeMessage, setTradeMessage] = useState(null); // { text, kind: 'buy' | 'sell' | 'error' }
    const [isHovered, setIsHovered] = useState(false);
    const [isLightboxOpen, setIsLightboxOpen] = useState(false);
    const [searchAttempts, setSearchAttempts] = useState(0);
    const [searchResult, setSearchResult] = useState(null); // { success, roll, modifier, dc, total }
    const [itemFound, setItemFound] = useState(false);
    const [restResult, setRestResult] = useState(null); // { restType, healingResults: [{ name, before, after, maxHP }] }
    const [resurrectionResult, setResurrectionResult] = useState(null); // { heroName, hpRestored, goldCost }
    const [showAllNpcs, setShowAllNpcs] = useState(false);

    if (!building) return null;

    const isResidential = RESIDENTIAL_TYPES.includes(building.buildingType);
    const isTemple = building.buildingType === 'temple';
    const canRest = REST_BUILDING_TYPES.includes(building.buildingType);
    const restType = building.buildingType === 'inn' ? 'long' : 'short';
    const isShop = SHOP_BUILDING_TYPES.includes(building.buildingType);
    const shopTab = isShop && (onBuy || onSell); // shops get a Wares tab alongside Visit
    const shopStock = isShop ? getShopStock(building.buildingType) : [];
    // Aggregate identical items into one entry per key (non-stackable items like rope
    // produce multiple inventory entries sharing a key; separate rows would collide on the
    // React key and break reconciliation). `count` is the total units held of that key.
    // Copies of each key the lead hero is currently wearing (those can't be sold).
    const equippedKeyCounts = {};
    if (isShop && firstHero) {
        Object.values(firstHero.equipment || {}).forEach((k) => {
            if (k) equippedKeyCounts[k] = (equippedKeyCounts[k] || 0) + 1;
        });
    }
    const sellableInventory = isShop && firstHero
        ? Object.values(
            (firstHero.inventory || [])
                .filter(item => item.key && ITEM_CATALOG[item.key] && isSellable(item.key))
                .reduce((acc, item) => {
                    if (!acc[item.key]) acc[item.key] = { ...item, count: 0 };
                    acc[item.key].count += item.quantity || 1;
                    return acc;
                }, {})
          )
          // Drop equipped copies from the sellable count; hide keys with no free copy.
          .map(row => ({ ...row, count: row.count - (equippedKeyCounts[row.key] || 0) }))
          .filter(row => row.count > 0)
          .sort((a, b) => {
              // Stable order: rows never jump as stacks deplete.
              const an = a.name || ITEM_CATALOG[a.key]?.name || a.key;
              const bn = b.name || ITEM_CATALOG[b.key]?.name || b.key;
              return an.localeCompare(bn);
          })
        : [];

    const handleBuy = (key) => {
        if (!onBuy) return;
        const result = onBuy(key);
        if (result && result.ok) {
            setTradeMessage({ text: `Bought ${ITEM_CATALOG[key]?.name || key} for ${buyPrice(key)} GP.`, kind: 'buy' });
        } else {
            setTradeMessage({ text: 'You cannot afford that.', kind: 'error' });
        }
    };

    const handleSell = (key) => {
        if (!onSell) return;
        const result = onSell(key);
        if (result && result.ok) {
            setTradeMessage({ text: `Sold ${ITEM_CATALOG[key]?.name || key} for ${result.gold} GP.`, kind: 'sell' });
        } else {
            setTradeMessage({ text: 'That item cannot be sold here.', kind: 'error' });
        }
    };
    const partyNeedsHealing = party && party.some(h => h.currentHP < h.maxHP && !h.isDefeated);
    const defeatedHeroes = party ? party.filter(h => h.isDefeated || h.currentHP <= 0) : [];
    const totalGold = party ? party.reduce((sum, h) => sum + (h.gold || 0), 0) : 0;

    // Map building types to image names
    const getImageSrc = (type, bld) => {
        if (type === 'house') {
            const variant = ((bld.x || 0) + (bld.y || 0)) % 3 + 1;
            return `/assets/buildings/house_interior_${variant}.webp`;
        }
        if (type === 'manor') {
            return `/assets/buildings/manor_interior_1.webp`;
        }
        const normalizedType = type.toLowerCase().replace(/\s+/g, '_');
        return `/assets/buildings/${normalizedType}.webp`;
    };

    const imageSrc = getImageSrc(building.buildingType, building);

    const toggleLightbox = (e) => {
        if (e) e.stopPropagation();
        setIsLightboxOpen(!isLightboxOpen);
    };

    // Sort residential NPCs: head first, then spouse, then children
    const sortedNpcs = isResidential
        ? [...npcs].sort((a, b) => {
            const order = { head: 0, spouse: 1, child: 2 };
            return (order[a.familyRole] ?? 3) - (order[b.familyRole] ?? 3);
        })
        : npcs;

    // Deduplicate NPCs who both work and live here (same NPC matched by both location and homeCoords)
    const uniqueNpcs = [];
    const seenIds = new Set();
    for (const npc of sortedNpcs) {
        if (!seenIds.has(npc.id)) {
            seenIds.add(npc.id);
            uniqueNpcs.push(npc);
        }
    }

    // --- Deterministic NPC-talk milestones ('talk' type) ---
    // A placed NPC carrying a milestoneNpcId that matches an active talk milestone gets
    // a "Talk" button. If this building IS the milestone's authored building but the
    // canonical NPC was never placed (old cached towns / placement miss), offer a
    // building-level "Ask for..." fallback that fires the same npc_talked event.
    const activeTalkMilestones = onTalkToNpc ? getActiveTalkMilestones(milestones) : [];
    const talkMilestoneForNpc = (npc) =>
        npc?.milestoneNpcId
            ? activeTalkMilestones.find(m => m.trigger.npc === npc.milestoneNpcId)
            : null;

    const buildingMatchesTalkMilestone = (m) => {
        const b = m.building;
        if (!b) return false;
        // Wrong town → no fallback (every town can have a barracks/tavern of the same type).
        if (b.location && townName &&
            String(b.location).toLowerCase() !== String(townName).toLowerCase()) return false;
        if (b.name && building.buildingName) {
            return b.name.toLowerCase() === String(building.buildingName).toLowerCase();
        }
        return !!b.type && b.type === building.buildingType;
    };

    const fallbackTalkMilestones = activeTalkMilestones.filter(m =>
        buildingMatchesTalkMilestone(m) &&
        !uniqueNpcs.some(n => n.milestoneNpcId === m.trigger.npc)
    );

    return (
        <>
            <div className="modal-overlay" onClick={onClose} style={{ zIndex: 1000 }}>
                <div
                    className="modal-content building-modal"
                    onClick={(e) => e.stopPropagation()}
                    style={{
                        maxWidth: '780px',
                        width: '95%',
                        padding: '0',
                        overflow: 'hidden',
                        maxHeight: '90vh',
                        display: 'flex',
                        flexDirection: 'column',
                        backgroundColor: 'var(--surface)',
                        color: 'var(--text)',
                        borderRadius: '12px'
                    }}
                >
                    {/* Header Section - Above the image */}
                    <div style={{ padding: '25px 25px 15px 25px', textAlign: 'center' }}>
                        {/* Name as the title; the type moves into the eyebrow above it. */}
                        <div style={{
                            fontFamily: 'var(--font-ui)',
                            fontSize: '0.78rem',
                            fontWeight: 700,
                            letterSpacing: '0.16em',
                            textTransform: 'uppercase',
                            color: 'var(--gold)',
                            marginBottom: '6px'
                        }}>
                            {building.buildingType} · ({building.x}, {building.y})
                        </div>
                        <h2 style={{
                            color: 'var(--text)',
                            margin: 0,
                            fontSize: '1.9rem',
                            letterSpacing: 'normal',
                            textTransform: 'none',
                            fontFamily: 'var(--font-display)'
                        }}>
                            {building.buildingName ? building.buildingName : (building.buildingType.charAt(0).toUpperCase() + building.buildingType.slice(1))}
                        </h2>
                    </div>

                    {/* Image Section - real image or placeholder for houses */}
                    {!imageError ? (
                        <div
                            style={{
                                width: 'calc(100% - 50px)',
                                height: '365px',
                                margin: '0 25px 20px 25px',
                                backgroundColor: '#000',
                                position: 'relative',
                                overflow: 'hidden',
                                borderRadius: '10px',
                                border: isHovered ? '3px solid var(--primary)' : '3px solid var(--border)',
                                cursor: 'pointer',
                                transition: 'all 0.3s ease',
                                transform: isHovered ? 'scale(1.005)' : 'scale(1)',
                                boxShadow: isHovered ? '0 0 20px var(--primary)' : 'none'
                            }}
                            onMouseEnter={() => setIsHovered(true)}
                            onMouseLeave={() => setIsHovered(false)}
                            onClick={toggleLightbox}
                            title="Click to view larger image"
                        >
                            <img
                                src={imageSrc}
                                alt={building.buildingType}
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                    opacity: isHovered ? 0.85 : 1,
                                    transition: 'opacity 0.3s ease'
                                }}
                                onError={() => setImageError(true)}
                            />
                            {isHovered && (
                                <div style={{
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    color: 'var(--bg)',
                                    backgroundColor: 'var(--primary)',
                                    padding: '10px 20px',
                                    borderRadius: '25px',
                                    fontSize: '0.9rem',
                                    fontWeight: 'bold',
                                    pointerEvents: 'none',
                                    border: '1px solid var(--bg)',
                                    boxShadow: '0 4px 10px rgba(0,0,0,0.3)'
                                }}>
                                    View Full Size
                                </div>
                            )}
                        </div>
                    ) : (
                        /* Placeholder when no image exists */
                        <div style={{
                            width: 'calc(100% - 50px)',
                            height: '160px',
                            margin: '0 25px 20px 25px',
                            background: 'linear-gradient(135deg, rgba(139,90,43,0.15) 0%, rgba(101,67,33,0.25) 100%)',
                            borderRadius: '10px',
                            border: '3px solid var(--border)',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px'
                        }}>
                            <span style={{ fontSize: '3.5rem' }}>
                                {building.buildingType === 'house' ? '\uD83C\uDFE0' :
                                 building.buildingType === 'manor' ? '\uD83C\uDFF0' :
                                 building.buildingType === 'keep' ? '\uD83C\uDFF0' : '\uD83C\uDFE2'}
                            </span>
                            <span style={{
                                fontSize: '0.85rem',
                                color: 'var(--text-secondary)',
                                fontStyle: 'italic',
                                fontFamily: 'var(--body-font)'
                            }}>
                                {isResidential ? 'A humble dwelling in town' : building.buildingType}
                            </span>
                        </div>
                    )}

                    <div style={{ padding: '0 25px 25px 25px', flex: 1, minHeight: 0, overflowY: 'auto' }}>
                        {/* Section tabs — only shops need them today (Wares on its own tab so the
                            modal stays compact and the Close button is always reachable). */}
                        {shopTab && (
                            <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
                                <button type="button" onClick={() => setActiveTab('visit')} style={tabBtnStyle(activeTab === 'visit')}>Visit</button>
                                <button type="button" onClick={() => setActiveTab('wares')} style={tabBtnStyle(activeTab === 'wares')}>Wares</button>
                            </div>
                        )}

                        {(!shopTab || activeTab === 'visit') && (
                          <>
                        {/* Inhabitants Section - enhanced for residential buildings */}
                        <div className="modal-section" style={{
                            backgroundColor: 'rgba(0,0,0,0.03)',
                            padding: '20px',
                            borderRadius: '10px',
                            border: '1px solid var(--border)'
                        }}>
                            <h4 style={{
                                borderBottom: '2px solid var(--primary)',
                                paddingBottom: '10px',
                                margin: '0 0 15px 0',
                                color: 'var(--primary)',
                                fontFamily: 'var(--header-font)'
                            }}>
                                {isResidential ? 'Household' : 'Inhabitants & Workers'}
                            </h4>
                            {uniqueNpcs.length > 0 ? (
                                <>
                                <ul style={{ listStyle: 'none', padding: 0, margin: '0' }}>
                                    {(showAllNpcs ? uniqueNpcs : uniqueNpcs.slice(0, 4)).map(npc => {
                                        const roleLabel = isResidential ? familyRoleLabel(npc) : null;
                                        const isWorksElsewhere = isResidential &&
                                            npc.location && npc.location.homeCoords &&
                                            (npc.location.x !== npc.location.homeCoords.x || npc.location.y !== npc.location.homeCoords.y);

                                        return (
                                            <li key={npc.id} style={{
                                                padding: '10px 0',
                                                borderBottom: '1px dashed var(--border)',
                                            }}>
                                                <div style={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center'
                                                }}>
                                                    <span style={{ fontWeight: 'bold', color: 'var(--text)' }}>
                                                        {genderIcon(npc.gender)} {npc.name}
                                                    </span>
                                                    <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                        {(() => {
                                                            // Talk button for the milestone NPC (active 'talk'
                                                            // milestone only — hides once completed).
                                                            const talkMs = talkMilestoneForNpc(npc);
                                                            if (!talkMs) return null;
                                                            return (
                                                                <button
                                                                    className="primary-button quest-talk-button"
                                                                    onClick={() => { onTalkToNpc(npc.milestoneNpcId, npc); onClose(); }}
                                                                    style={{ padding: '4px 12px', fontWeight: 'bold', fontSize: '12px' }}
                                                                    title={talkMs.text}
                                                                >
                                                                    📜 Quest: Talk to {npc.name}
                                                                </button>
                                                            );
                                                        })()}
                                                        <span style={{
                                                            fontSize: '12px',
                                                            color: 'var(--text-secondary)',
                                                            backgroundColor: 'var(--border)',
                                                            padding: '4px 12px',
                                                            borderRadius: '6px',
                                                            fontWeight: '500'
                                                        }}>
                                                            {/* Cached towns baked NPC jobs before civic buildings had name
                                                                fallbacks; strip the stale "of undefined" tail so old saves
                                                                read "Harbormaster", not "Harbormaster of undefined". */}
                                                            {(npc.job || npc.title || 'Resident').replace(/ (of|at) undefined$/, '')}
                                                        </span>
                                                    </span>
                                                </div>
                                                {/* Extra details for residential buildings */}
                                                {isResidential && (
                                                    <div style={{
                                                        display: 'flex',
                                                        gap: '12px',
                                                        marginTop: '4px',
                                                        fontSize: '0.8rem',
                                                        color: 'var(--text-secondary)',
                                                        fontFamily: 'var(--body-font)'
                                                    }}>
                                                        {roleLabel && <span>{roleLabel}</span>}
                                                        {npc.age && <span>Age {npc.age}</span>}
                                                        {npc.race && <span>{npc.race}</span>}
                                                        {isWorksElsewhere && (
                                                            <span style={{ fontStyle: 'italic' }}>
                                                                (works elsewhere)
                                                            </span>
                                                        )}
                                                    </div>
                                                )}
                                            </li>
                                        );
                                    })}
                                </ul>
                                {uniqueNpcs.length > 4 && (
                                    <button
                                        onClick={() => setShowAllNpcs(!showAllNpcs)}
                                        style={{
                                            display: 'block',
                                            width: '100%',
                                            marginTop: '8px',
                                            padding: '6px',
                                            background: 'none',
                                            border: '1px dashed var(--border)',
                                            borderRadius: '6px',
                                            color: 'var(--primary)',
                                            cursor: 'pointer',
                                            fontSize: '0.85rem',
                                            fontFamily: 'var(--body-font)'
                                        }}
                                    >
                                        {showAllNpcs
                                            ? 'Show less'
                                            : `Show all (${uniqueNpcs.length - 4} more)`}
                                    </button>
                                )}
                                </>
                            ) : (
                                <p style={{
                                    fontStyle: 'italic',
                                    color: 'var(--text-secondary)',
                                    textAlign: 'center',
                                    margin: '20px 0',
                                    fontFamily: 'var(--body-font)'
                                }}>
                                    No one seems to be here right now.
                                </p>
                            )}
                        </div>

                        {/* Milestone talk fallback — this is the milestone's authored building but
                            the canonical NPC isn't among the placed occupants (older cached towns).
                            Asking for them fires the same npc_talked event as the Talk button. */}
                        {fallbackTalkMilestones.length > 0 && (
                            <div className="modal-section" style={{
                                backgroundColor: 'rgba(0,0,0,0.03)',
                                padding: '20px',
                                borderRadius: '10px',
                                border: '2px solid var(--accent, var(--primary))',
                                marginTop: '15px'
                            }}>
                                <h4 style={{
                                    borderBottom: '2px solid var(--accent, var(--primary))',
                                    paddingBottom: '10px',
                                    margin: '0 0 15px 0',
                                    color: 'var(--accent, var(--primary))',
                                    fontFamily: 'var(--header-font)'
                                }}>
                                    Seek Someone Out
                                </h4>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                    {fallbackTalkMilestones.map(m => (
                                        <button
                                            key={m.id}
                                            className="primary-button quest-talk-button"
                                            onClick={() => { onTalkToNpc(m.trigger.npc, null); onClose(); }}
                                            title={m.text}
                                            style={{ width: '100%', padding: '12px', fontWeight: 'bold', letterSpacing: '1px' }}
                                        >
                                            📜 Quest: Ask for {m.spawn?.name || `the ${m.spawn?.role || 'contact'}`}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Quest Item Search Section — offered only while the stamp matches a
                            CURRENT, uncompleted item milestone. questItemId stays stamped in the
                            cached town map forever, and with in-save continuation a save outlives
                            the campaign that stamped it: completed-milestone stamps AND stale
                            previous-campaign stamps are both hidden (isQuestItemSearchable). */}
                        {building.questItemId && !itemFound && isQuestItemSearchable(milestones, building.questItemId) && (
                            <div className="modal-section" style={{
                                backgroundColor: 'rgba(0,0,0,0.03)',
                                padding: '20px',
                                borderRadius: '10px',
                                border: '2px solid var(--accent, var(--primary))',
                                marginTop: '15px'
                            }}>
                                <h4 style={{
                                    borderBottom: '2px solid var(--accent, var(--primary))',
                                    paddingBottom: '10px',
                                    margin: '0 0 15px 0',
                                    color: 'var(--accent, var(--primary))',
                                    fontFamily: 'var(--header-font)'
                                }}>
                                    Search for {building.questItemName || 'Quest Item'}
                                </h4>
                                <p style={{ margin: '0 0 15px 0', color: 'var(--text-secondary)', fontFamily: 'var(--body-font)' }}>
                                    {searchAttempts === 0
                                        ? 'You sense something important may be hidden here. Search the building to find it.'
                                        : `You've searched ${searchAttempts} time${searchAttempts > 1 ? 's' : ''}. Keep looking...`}
                                </p>
                                {searchResult && !searchResult.success && (
                                    <div style={{
                                        padding: '12px',
                                        marginBottom: '15px',
                                        borderRadius: '8px',
                                        backgroundColor: 'rgba(200, 100, 50, 0.1)',
                                        border: '1px solid rgba(200, 100, 50, 0.3)',
                                        fontFamily: 'var(--body-font)',
                                        fontSize: '0.9rem'
                                    }}>
                                        <strong>Roll: {searchResult.roll} + {searchResult.modifier} = {searchResult.total}</strong> (needed {searchResult.dc})
                                        <br />
                                        <span style={{ fontStyle: 'italic', color: 'var(--text-secondary)' }}>
                                            {searchResult.total <= searchResult.dc - 5
                                                ? 'You rummage through shelves and drawers but find nothing of note. Perhaps try a different area.'
                                                : 'You notice something promising but can\'t quite locate it. You\'re getting closer...'}
                                        </span>
                                    </div>
                                )}
                                <button
                                    className="primary-button"
                                    onClick={() => {
                                        const baseDC = 12;
                                        const dcReduction = searchAttempts * 3;
                                        const dc = Math.max(2, baseDC - dcReduction);
                                        const stats = firstHero?.stats || {};
                                        const intMod = getAbilityModifier(stats.Intelligence || stats.intelligence);
                                        const wisMod = getAbilityModifier(stats.Wisdom || stats.wisdom);
                                        const modifier = Math.max(intMod, wisMod);
                                        const roll = Math.floor(Math.random() * 20) + 1;
                                        const total = roll + modifier;
                                        const success = total >= dc;
                                        const newAttempts = searchAttempts + 1;
                                        setSearchAttempts(newAttempts);
                                        setSearchResult({ success, roll, modifier, dc, total });
                                        if (success) {
                                            setItemFound(true);
                                            if (onQuestItemFound) {
                                                onQuestItemFound(building.questItemId, building.questItemName);
                                            }
                                        }
                                    }}
                                    style={{
                                        width: '100%',
                                        padding: '12px',
                                        fontWeight: 'bold',
                                        letterSpacing: '1px'
                                    }}
                                >
                                    Search the {building.buildingType || 'Building'}
                                </button>
                            </div>
                        )}

                        {/* Quest Item Found */}
                        {itemFound && searchResult && (
                            <div className="modal-section" style={{
                                backgroundColor: 'rgba(50, 180, 80, 0.1)',
                                padding: '20px',
                                borderRadius: '10px',
                                border: '2px solid rgba(50, 180, 80, 0.6)',
                                marginTop: '15px',
                                textAlign: 'center'
                            }}>
                                <h4 style={{
                                    margin: '0 0 10px 0',
                                    color: 'rgba(50, 180, 80, 1)',
                                    fontFamily: 'var(--header-font)'
                                }}>
                                    Item Found!
                                </h4>
                                <p style={{ margin: '0 0 8px 0', fontFamily: 'var(--body-font)' }}>
                                    <strong>Roll: {searchResult.roll} + {searchResult.modifier} = {searchResult.total}</strong> (needed {searchResult.dc})
                                </p>
                                <p style={{ margin: 0, fontFamily: 'var(--body-font)', fontSize: '1.1rem' }}>
                                    You discovered <strong style={{ color: 'var(--primary)' }}>{building.questItemName || 'a quest item'}</strong>!
                                </p>
                            </div>
                        )}

                        {/* Turn-in Section - hand in completed quests (return-to-giver / courier) */}
                        {onTurnInQuest && (() => {
                            const ctx = { buildingType: building.buildingType, townName };
                            const ready = getReadyTurnIns(sideQuests || [], ctx);
                            if (ready.length === 0) return null;
                            // Multi-step quests can have errands partway through, so name the step.
                            const readyStepText = (q, c) => (getReadyTurnInStep(q, c) || {}).text || '';
                            return (
                                <div className="modal-section" style={{ backgroundColor: 'rgba(0,0,0,0.03)', padding: '20px', borderRadius: '10px', border: '1px solid var(--state-success, #4caf50)', marginTop: '15px' }}>
                                    <h4 style={{ borderBottom: '2px solid var(--state-success, #4caf50)', paddingBottom: '10px', margin: '0 0 15px 0', color: 'var(--state-success, #4caf50)', fontFamily: 'var(--header-font)' }}>
                                        ✅ Completed Tasks
                                    </h4>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                        {ready.map(q => (
                                            <div key={q.id} style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: '8px', padding: '12px 14px' }}>
                                                <div style={{ fontWeight: 700, color: 'var(--text)', marginBottom: '4px' }}>{q.title}</div>
                                                {readyStepText(q, ctx) && (
                                                    <div style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', marginBottom: '8px' }}>{readyStepText(q, ctx)}</div>
                                                )}
                                                <button className="primary-button" onClick={() => onTurnInQuest(ctx)}>Turn In</button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            );
                        })()}

                        {/* Quest-giver Section - rumours & tasks offered by THIS building's type */}
                        {onAcceptSideQuest && (() => {
                            const available = getOfferAt(sideQuests || [], { buildingType: building.buildingType, townName, level: effectivePartyLevel(party) });
                            if (available.length === 0) return null;
                            return (
                                <div className="modal-section" style={{ backgroundColor: 'rgba(0,0,0,0.03)', padding: '20px', borderRadius: '10px', border: '1px solid var(--border)', marginTop: '15px' }}>
                                    <h4 style={{ borderBottom: '2px solid var(--primary)', paddingBottom: '10px', margin: '0 0 15px 0', color: 'var(--primary)', fontFamily: 'var(--header-font)' }}>
                                        📜 Rumours &amp; Tasks
                                    </h4>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                        {available.map(q => (
                                            <div key={q.id} style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: '8px', padding: '12px 14px' }}>
                                                <div style={{ fontWeight: 700, color: 'var(--text)', marginBottom: '4px' }}>{q.title}</div>
                                                <div style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', fontStyle: 'italic', marginBottom: '10px' }}>"{q.giver?.hook || q.description}"</div>
                                                <button
                                                    className="primary-button"
                                                    // Anchor the quest to THIS building, so its hand-in names
                                                    // it (not the first building of its type in town).
                                                    onClick={() => onAcceptSideQuest(q.id, townName ? { town: townName, buildingName: building.buildingName || null } : null)}
                                                >Accept Quest</button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            );
                        })()}

                        {/* Rest Section - for inns and taverns */}
                        {canRest && onRest && !restResult && (
                            <div className="modal-section" style={{
                                backgroundColor: 'rgba(0,0,0,0.03)',
                                padding: '20px',
                                borderRadius: '10px',
                                border: '1px solid var(--border)',
                                marginTop: '15px'
                            }}>
                                <h4 style={{
                                    borderBottom: '2px solid var(--primary)',
                                    paddingBottom: '10px',
                                    margin: '0 0 15px 0',
                                    color: 'var(--primary)',
                                    fontFamily: 'var(--header-font)'
                                }}>
                                    {restType === 'long' ? 'Rest at the Inn' : 'Take a Breather'}
                                </h4>
                                <p style={{
                                    margin: '0 0 15px 0',
                                    color: 'var(--text-secondary)',
                                    fontFamily: 'var(--body-font)',
                                    fontSize: '0.9rem'
                                }}>
                                    {restType === 'long'
                                        ? 'A warm bed and a good night\'s sleep will fully restore your party\'s health.'
                                        : 'A hot meal and a stiff drink will restore some of your party\'s vigor.'}
                                </p>
                                {/* Party HP preview */}
                                {party && party.length > 0 && (
                                    <div style={{ marginBottom: '15px' }}>
                                        {party.filter(h => !h.isDefeated).map(hero => {
                                            const name = hero.heroName || hero.characterName || 'Unknown';
                                            const id = hero.heroId || name;
                                            const hpStatus = getHPStatus(hero.currentHP, hero.maxHP);
                                            const pct = (hero.currentHP / hero.maxHP) * 100;
                                            return (
                                                <div key={id} style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '10px',
                                                    marginBottom: '6px',
                                                    fontSize: '0.85rem'
                                                }}>
                                                    <span style={{ width: '100px', fontWeight: 'bold', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</span>
                                                    <div style={{
                                                        flex: 1,
                                                        height: '10px',
                                                        background: 'var(--border)',
                                                        borderRadius: '5px',
                                                        overflow: 'hidden'
                                                    }}>
                                                        <div style={{
                                                            width: `${pct}%`,
                                                            height: '100%',
                                                            background: hpStatus.color,
                                                            borderRadius: '5px',
                                                            transition: 'width 0.3s ease'
                                                        }} />
                                                    </div>
                                                    <span style={{ color: hpStatus.color, fontWeight: 'bold', minWidth: '60px', textAlign: 'right' }}>
                                                        {hero.currentHP}/{hero.maxHP}
                                                    </span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                                <button
                                    className="primary-button"
                                    disabled={!partyNeedsHealing}
                                    onClick={() => {
                                        if (onRest) {
                                            const result = onRest(restType);
                                            if (result) setRestResult(result);
                                        }
                                    }}
                                    style={{
                                        width: '100%',
                                        padding: '12px',
                                        fontWeight: 'bold',
                                        letterSpacing: '1px',
                                        opacity: partyNeedsHealing ? 1 : 0.5
                                    }}
                                >
                                    {partyNeedsHealing
                                        ? (restType === 'long' ? 'Rest (Full Heal)' : 'Rest (Heal 50%)')
                                        : 'Party is at full health'}
                                </button>
                            </div>
                        )}

                        {/* Rest Result */}
                        {restResult && (
                            <div className="modal-section" style={{
                                backgroundColor: 'rgba(39, 174, 96, 0.1)',
                                padding: '20px',
                                borderRadius: '10px',
                                border: '2px solid rgba(39, 174, 96, 0.5)',
                                marginTop: '15px'
                            }}>
                                <h4 style={{
                                    margin: '0 0 15px 0',
                                    color: '#27ae60',
                                    fontFamily: 'var(--header-font)',
                                    textAlign: 'center'
                                }}>
                                    {restResult.restType === 'long' ? 'Well Rested!' : 'Feeling Refreshed!'}
                                </h4>
                                {restResult.healingResults.map(hr => {
                                    const healed = hr.after - hr.before;
                                    return (
                                        <div key={hr.name} style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '10px',
                                            marginBottom: '6px',
                                            fontSize: '0.85rem'
                                        }}>
                                            <span style={{ width: '100px', fontWeight: 'bold', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{hr.name}</span>
                                            <div style={{
                                                flex: 1,
                                                height: '10px',
                                                background: 'var(--border)',
                                                borderRadius: '5px',
                                                overflow: 'hidden'
                                            }}>
                                                <div style={{
                                                    width: `${(hr.after / hr.maxHP) * 100}%`,
                                                    height: '100%',
                                                    background: '#27ae60',
                                                    borderRadius: '5px',
                                                    transition: 'width 0.5s ease'
                                                }} />
                                            </div>
                                            <span style={{ color: '#27ae60', fontWeight: 'bold', minWidth: '90px', textAlign: 'right' }}>
                                                {hr.before} → {hr.after}
                                                {healed > 0 && <span style={{ color: '#27ae60', fontSize: '0.8rem' }}> (+{healed})</span>}
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {/* Resurrection Section - temples only */}
                        {isTemple && onResurrect && defeatedHeroes.length > 0 && !resurrectionResult && (
                            <div className="modal-section" style={{
                                backgroundColor: 'rgba(0,0,0,0.03)',
                                padding: '20px',
                                borderRadius: '10px',
                                border: '2px solid #8e44ad',
                                marginTop: '15px'
                            }}>
                                <h4 style={{
                                    borderBottom: '2px solid #8e44ad',
                                    paddingBottom: '10px',
                                    margin: '0 0 15px 0',
                                    color: '#8e44ad',
                                    fontFamily: 'var(--header-font)'
                                }}>
                                    Resurrection Rites
                                </h4>
                                <p style={{
                                    margin: '0 0 15px 0',
                                    color: 'var(--text-secondary)',
                                    fontFamily: 'var(--body-font)',
                                    fontSize: '0.9rem'
                                }}>
                                    The priests can perform a resurrection ritual to revive a fallen companion. They will be restored to half health.
                                </p>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                    {defeatedHeroes.map(hero => {
                                        const name = hero.heroName || hero.characterName || 'Unknown';
                                        const id = hero.heroId || name;
                                        const level = hero.level || hero.heroLevel || hero.characterLevel || 1;
                                        const cost = RESURRECTION_COST_PER_LEVEL * level;
                                        const canAfford = totalGold >= cost;
                                        return (
                                            <button
                                                key={id}
                                                disabled={!canAfford}
                                                onClick={() => {
                                                    const result = onResurrect(id, cost);
                                                    if (result) setResurrectionResult(result);
                                                }}
                                                style={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    padding: '12px 14px',
                                                    background: canAfford ? 'rgba(142, 68, 173, 0.1)' : 'rgba(0,0,0,0.05)',
                                                    border: canAfford ? '1px solid #8e44ad' : '1px solid var(--border)',
                                                    borderRadius: '8px',
                                                    color: canAfford ? 'var(--text)' : 'var(--text-secondary)',
                                                    cursor: canAfford ? 'pointer' : 'not-allowed',
                                                    fontFamily: 'var(--body-font)',
                                                    fontSize: '0.9rem',
                                                    opacity: canAfford ? 1 : 0.5
                                                }}
                                            >
                                                <span>
                                                    <span style={{ fontWeight: 'bold' }}>💀 {name}</span>
                                                    <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginLeft: '8px' }}>
                                                        Lvl {level}
                                                    </span>
                                                </span>
                                                <span style={{ fontWeight: 'bold', color: canAfford ? '#d4af37' : 'var(--text-secondary)' }}>
                                                    {cost} GP
                                                </span>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* Resurrection Result */}
                        {resurrectionResult && (
                            <div className="modal-section" style={{
                                backgroundColor: 'rgba(142, 68, 173, 0.1)',
                                padding: '20px',
                                borderRadius: '10px',
                                border: '2px solid rgba(142, 68, 173, 0.5)',
                                marginTop: '15px',
                                textAlign: 'center'
                            }}>
                                <h4 style={{
                                    margin: '0 0 10px 0',
                                    color: '#8e44ad',
                                    fontFamily: 'var(--header-font)'
                                }}>
                                    Resurrection Complete
                                </h4>
                                <p style={{ margin: 0, fontFamily: 'var(--body-font)', fontSize: '0.95rem' }}>
                                    {resurrectionResult.heroName} has been restored to life with <strong style={{ color: '#27ae60' }}>{resurrectionResult.hpRestored} HP</strong>.
                                </p>
                                <p style={{ margin: '6px 0 0 0', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                                    The ritual cost {resurrectionResult.goldCost} gold.
                                </p>
                            </div>
                        )}

                          </>
                        )}

                        {/* Wares Section - shops, markets, blacksmiths, alchemists (own tab) */}
                        {shopTab && activeTab === 'wares' && (
                            <div className="modal-section" style={{
                                backgroundColor: 'rgba(0,0,0,0.03)',
                                padding: '20px',
                                borderRadius: '10px',
                                border: '1px solid var(--border)',
                                marginTop: '15px'
                            }}>
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    borderBottom: '2px solid var(--primary)',
                                    paddingBottom: '10px',
                                    margin: '0 0 15px 0'
                                }}>
                                    <h4 style={{
                                        margin: 0,
                                        color: 'var(--primary)',
                                        fontFamily: 'var(--header-font)'
                                    }}>
                                        Wares
                                    </h4>
                                    <span style={{
                                        fontWeight: 'bold',
                                        color: '#d4af37',
                                        fontFamily: 'var(--body-font)'
                                    }}>
                                        Party Gold: {totalGold} GP
                                    </span>
                                </div>

                                {tradeMessage && (
                                    <div style={{
                                        padding: '10px 12px',
                                        marginBottom: '15px',
                                        borderRadius: '8px',
                                        fontFamily: 'var(--body-font)',
                                        fontSize: '0.85rem',
                                        backgroundColor: tradeMessage.kind === 'error'
                                            ? 'rgba(200, 100, 50, 0.1)'
                                            : 'rgba(39, 174, 96, 0.1)',
                                        border: tradeMessage.kind === 'error'
                                            ? '1px solid rgba(200, 100, 50, 0.3)'
                                            : '1px solid rgba(39, 174, 96, 0.4)',
                                        color: 'var(--text)'
                                    }}>
                                        {tradeMessage.text}
                                    </div>
                                )}

                                {/* Buy list */}
                                {onBuy && shopStock.length > 0 && (
                                    <>
                                        <h5 style={{
                                            margin: '0 0 8px 0',
                                            color: 'var(--text-secondary)',
                                            fontFamily: 'var(--body-font)',
                                            textTransform: 'uppercase',
                                            letterSpacing: '1px',
                                            fontSize: '0.75rem'
                                        }}>
                                            For Sale
                                        </h5>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '15px' }}>
                                            {shopStock.map(key => {
                                                const item = ITEM_CATALOG[key];
                                                if (!item) return null;
                                                const price = buyPrice(key);
                                                const affordable = canAfford(party, key);
                                                return (
                                                    <div key={`buy-${key}`} style={{
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        alignItems: 'center',
                                                        padding: '8px 12px',
                                                        background: 'rgba(0,0,0,0.03)',
                                                        border: '1px solid var(--border)',
                                                        borderRadius: '8px',
                                                        fontFamily: 'var(--body-font)',
                                                        fontSize: '0.9rem'
                                                    }}>
                                                        <span style={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
                                                            <span style={{ fontWeight: 'bold', color: getRarityColor(item.rarity) }}>{item.name}</span>
                                                            {shopEffectLabel(item) && (
                                                                <span style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>{shopEffectLabel(item)}</span>
                                                            )}
                                                        </span>
                                                        <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                            <span style={{ color: '#d4af37', fontWeight: 'bold', minWidth: '60px', textAlign: 'right' }}>
                                                                {price} GP
                                                            </span>
                                                            <button
                                                                className="primary-button"
                                                                disabled={!affordable}
                                                                onClick={() => handleBuy(key)}
                                                                style={{
                                                                    padding: '6px 14px',
                                                                    fontWeight: 'bold',
                                                                    cursor: affordable ? 'pointer' : 'not-allowed',
                                                                    opacity: affordable ? 1 : 0.5
                                                                }}
                                                            >
                                                                Buy
                                                            </button>
                                                        </span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </>
                                )}

                                {/* Sell list */}
                                {onSell && (
                                    <>
                                        <h5 style={{
                                            margin: '0 0 8px 0',
                                            color: 'var(--text-secondary)',
                                            fontFamily: 'var(--body-font)',
                                            textTransform: 'uppercase',
                                            letterSpacing: '1px',
                                            fontSize: '0.75rem'
                                        }}>
                                            Sell Your Loot
                                        </h5>
                                        {sellableInventory.length > 0 ? (
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                                {sellableInventory.map(item => {
                                                    const price = sellPrice(item.key);
                                                    const qty = item.count || item.quantity || 1;
                                                    return (
                                                        <div key={`sell-${item.key}`} style={{
                                                            display: 'flex',
                                                            justifyContent: 'space-between',
                                                            alignItems: 'center',
                                                            padding: '8px 12px',
                                                            background: 'rgba(0,0,0,0.03)',
                                                            border: '1px solid var(--border)',
                                                            borderRadius: '8px',
                                                            fontFamily: 'var(--body-font)',
                                                            fontSize: '0.9rem'
                                                        }}>
                                                            <span style={{ fontWeight: 'bold', color: getRarityColor(ITEM_CATALOG[item.key]?.rarity || item.rarity) }}>
                                                                {item.name || ITEM_CATALOG[item.key]?.name || item.key}
                                                                {qty > 1 && (
                                                                    <span style={{ color: 'var(--text-secondary)', fontWeight: 'normal' }}> x{qty}</span>
                                                                )}
                                                            </span>
                                                            <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                                <span style={{ color: '#d4af37', fontWeight: 'bold', minWidth: '60px', textAlign: 'right' }}>
                                                                    {price} GP
                                                                </span>
                                                                <button
                                                                    className="secondary-button"
                                                                    onClick={() => handleSell(item.key)}
                                                                    style={{
                                                                        padding: '6px 14px',
                                                                        fontWeight: 'bold',
                                                                        cursor: 'pointer'
                                                                    }}
                                                                >
                                                                    Sell
                                                                </button>
                                                            </span>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        ) : (
                                            <p style={{
                                                fontStyle: 'italic',
                                                color: 'var(--text-secondary)',
                                                fontFamily: 'var(--body-font)',
                                                fontSize: '0.85rem',
                                                margin: '4px 0 0 0'
                                            }}>
                                                You have nothing to sell.
                                            </p>
                                        )}
                                    </>
                                )}
                            </div>
                        )}

                        <button
                            className="secondary-button"
                            onClick={onClose}
                            style={{
                                width: '100%',
                                marginTop: '25px',
                                padding: '14px',
                                fontWeight: 'bold',
                                letterSpacing: '2px',
                                fontSize: '1rem'
                            }}
                        >
                            Close
                        </button>
                    </div>
                </div>
            </div>

            {/* Image Lightbox Modal */}
            {isLightboxOpen && !imageError && (
                <div
                    className="modal-overlay lightbox-overlay"
                    onClick={toggleLightbox}
                    style={{
                        zIndex: 3000,
                        backgroundColor: 'rgba(0,0,0,0.9)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'zoom-out'
                    }}
                >
                    <div
                        style={{
                            position: 'relative',
                            maxWidth: '90vw',
                            maxHeight: '90vh',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            onClick={toggleLightbox}
                            style={{
                                position: 'absolute',
                                top: '-40px',
                                right: '-40px',
                                background: 'none',
                                border: 'none',
                                color: 'white',
                                fontSize: '32px',
                                cursor: 'pointer',
                                padding: '10px',
                                lineHeight: '1'
                            }}
                            aria-label="Close lightbox"
                        >
                            ✕
                        </button>
                        <img
                            src={imageSrc}
                            alt={building.buildingType}
                            style={{
                                maxWidth: '100%',
                                maxHeight: '85vh',
                                borderRadius: '8px',
                                border: '2px solid var(--primary)',
                                boxShadow: '0 0 30px rgba(0,0,0,0.8)'
                            }}
                        />
                        <div style={{
                            marginTop: '15px',
                            color: 'white',
                            fontFamily: 'var(--header-font)',
                            fontSize: '1.2rem',
                            letterSpacing: '1px',
                            textShadow: '2px 2px 4px rgba(0,0,0,0.5)'
                        }}>
                            {building.buildingName ? building.buildingName : (building.buildingType.charAt(0).toUpperCase() + building.buildingType.slice(1))}{building.buildingName && building.buildingType ? ` (${building.buildingType.charAt(0).toUpperCase() + building.buildingType.slice(1)})` : ''}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default BuildingModal;
